import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package, X, ChevronDown, ChevronUp, Save, Upload, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { toast } from 'react-toastify';

const CATEGORIES = ['sand', 'cement', 'iron', 'bricks', 'long_bricks', 'short_bricks', 'other'];
const PRESETS = {
  sand: { units: ['load', 'cubic meter', 'ton', 'brass'], specs: ['Type', 'Grade', 'Source'], variants: [{ size: 'Fine Sand', unit: 'load' }, { size: 'Coarse Sand', unit: 'load' }, { size: 'M-Sand', unit: 'ton' }] },
  cement: { units: ['bag', 'ton', 'piece'], specs: ['Grade', 'Type', 'Weight'], variants: [{ size: '50kg Bag', unit: 'bag' }, { size: '25kg Bag', unit: 'bag' }] },
  iron: { units: ['piece', 'kg', 'ton', 'bundle'], specs: ['Grade', 'Diameter', 'Length', 'Standard'], variants: [{ size: '8mm TMT Rod', unit: 'piece' }, { size: '10mm TMT Rod', unit: 'piece' }, { size: '12mm TMT Rod', unit: 'piece' }, { size: '16mm TMT Rod', unit: 'piece' }, { size: '20mm TMT Rod', unit: 'piece' }, { size: '25mm TMT Rod', unit: 'piece' }] },
  bricks: { units: ['piece', '1000 pieces', 'lot'], specs: ['Type', 'Size', 'Strength'], variants: [{ size: 'Standard Brick (9×4×3)', unit: 'piece' }, { size: 'Fly Ash Brick', unit: 'piece' }, { size: 'AAC Block', unit: 'piece' }] },
  long_bricks: { units: ['piece', '1000 pieces'], specs: ['Type', 'Size'], variants: [{ size: 'Hollow Long Brick', unit: 'piece' }, { size: 'Solid Long Brick', unit: 'piece' }] },
  short_bricks: { units: ['piece', '1000 pieces'], specs: ['Type', 'Size'], variants: [{ size: 'Paving Brick', unit: 'piece' }, { size: 'Interlocking Brick', unit: 'piece' }] },
  other: { units: ['piece', 'kg', 'lot', 'unit'], specs: ['Type'], variants: [] },
};

const EMPTY_FORM = { name: '', category: 'sand', brand: '', description: '', price: '', unit: 'load', stock: '', lowStockThreshold: 5, images: [], tags: '', variants: [], specifications: [], offer: { discount: '', validTill: '' } };

export default function OwnerMaterials() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [bulkText, setBulkText] = useState('');

  useEffect(() => { fetchMaterials(); }, []);

  const fetchMaterials = () => {
    setLoading(true);
    API.get('/materials/my-materials').then(r => setMaterials(r.data)).finally(() => setLoading(false));
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyPreset = (cat) => {
    const preset = PRESETS[cat] || PRESETS.other;
    setForm(f => ({ ...f, category: cat, unit: preset.units[0], variants: [], specifications: preset.specs.map(s => ({ key: s, value: '' })) }));
  };

  const addVariant = () => setForm(f => ({ ...f, variants: [...f.variants, { size: '', unit: f.unit, price: '', stock: '', sku: '' }] }));
  const updateVariant = (i, k, v) => setForm(f => { const vs = [...f.variants]; vs[i] = { ...vs[i], [k]: v }; return { ...f, variants: vs }; });
  const removeVariant = (i) => setForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const loadPresetVariants = () => {
    const preset = PRESETS[form.category];
    if (preset?.variants?.length) setForm(f => ({ ...f, variants: preset.variants.map(v => ({ ...v, price: '', stock: '', sku: '' })) }));
  };

  const updateSpec = (i, k, v) => setForm(f => { const ss = [...f.specifications]; ss[i] = { ...ss[i], [k]: v }; return { ...f, specifications: ss }; });

  const addImages = (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const valid = selected.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} must be 2MB or smaller`);
        return false;
      }
      return true;
    });
    const remaining = Math.max(0, 8 - (form.images?.length || 0));
    if (valid.length > remaining) toast.info(`Only ${remaining} more image${remaining === 1 ? '' : 's'} allowed`);
    valid.slice(0, remaining).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => setForm(f => ({ ...f, images: [...(f.images || []), e.target.result] }));
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => setForm(f => ({ ...f, images: (f.images || []).filter((_, i) => i !== index) }));

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowVariants(false);
    setShowForm(true);
  };

  const openEdit = (mat) => {
    setForm({
      name: mat.name, category: mat.category, brand: mat.brand || '', description: mat.description || '',
      price: mat.price, unit: mat.unit, stock: mat.stock, lowStockThreshold: mat.lowStockThreshold ?? 5, images: mat.images || [], tags: mat.tags?.join(', ') || '',
      variants: mat.variants || [], specifications: mat.specifications || [],
      offer: { discount: mat.offer?.discount || '', validTill: mat.offer?.validTill?.slice(0, 10) || '' }
    });
    setEditId(mat._id);
    setShowVariants(mat.variants?.length > 0);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price || !form.stock) { toast.error('Name, price and stock are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        lowStockThreshold: Math.max(0, Number(form.lowStockThreshold) || 0),
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        variants: form.variants.map(v => ({ ...v, price: Number(v.price), stock: Number(v.stock) })),
        offer: form.offer.discount ? { discount: Number(form.offer.discount), validTill: form.offer.validTill || undefined } : undefined
      };
      if (editId) await API.put(`/materials/${editId}`, payload);
      else await API.post('/materials', payload);
      toast.success(editId ? 'Material updated!' : 'Material added!');
      setShowForm(false);
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try { await API.delete(`/materials/${id}`); setMaterials(prev => prev.filter(m => m._id !== id)); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const toggleAvailability = async (material) => {
    try {
      const nextValue = material.isAvailable === false;
      const res = await API.put(`/materials/${material._id}/availability`, { isAvailable: nextValue });
      setMaterials(prev => prev.map(item => item._id === material._id ? res.data : item));
      toast.success(nextValue ? 'Material is now visible to customers' : 'Material hidden from customers');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update visibility');
    }
  };

  const handleBulkImport = async () => {
    try {
      const materialsToAdd = bulkText.split('\n').map(line => {
        const [name, category, price, unit, stock, brand] = line.split(',').map(v => v?.trim());
        return { name, category: category || 'other', price, unit: unit || 'piece', stock, brand };
      }).filter(row => row.name && row.price);
      const res = await API.post('/materials/bulk', { materials: materialsToAdd });
      toast.success(`${res.data.count} materials imported`);
      setBulkText('');
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk import failed');
    }
  };

  const filtered = filterCat ? materials.filter(m => m.category === filterCat) : materials;
  const lowStock = materials.filter(m => Number(m.stock || 0) <= Number(m.lowStockThreshold ?? 5));
  const preset = PRESETS[form.category] || PRESETS.other;

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-bg)" }} className="page-with-bottom-nav">
      <Navbar />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>My Materials</h1>
            <p style={{ color: 'var(--gray)', fontSize: 14 }}>{materials.length} materials listed</p>
          </div>
          <button onClick={openAdd} className="btn-primary" style={{ padding: '11px 22px' }}>
            <Plus size={16} /> Add Material
          </button>
        </div>

        {/* Category filter */}
        {lowStock.length > 0 && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '11px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, color: '#92400E', fontSize: 13, fontWeight: 700 }}>
            <AlertCircle size={15} /> Low stock: {lowStock.map(m => `${m.name} (${m.stock})`).slice(0, 4).join(', ')}
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 14, padding: 14, boxShadow: 'var(--shadow)', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
            <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 }}><Upload size={15} color="var(--orange)" /> Bulk Import</div>
            <button onClick={handleBulkImport} className="btn-outline" style={{ padding: '7px 12px', fontSize: 12 }} disabled={!bulkText.trim()}>Import</button>
          </div>
          <textarea className="form-input" rows={3} placeholder="One material per line: Name, category, price, unit, stock, brand"
            value={bulkText} onChange={e => setBulkText(e.target.value)} style={{ resize: 'vertical' }} />
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {[{ key: '', label: 'All' }, ...CATEGORIES.map(c => ({ key: c, label: c.replace('_', ' ') }))].map(cat => (
            <button key={cat.key} onClick={() => setFilterCat(cat.key)}
              style={{ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${filterCat === cat.key ? 'var(--orange)' : 'var(--gray-border)'}`, background: filterCat === cat.key ? 'var(--orange-soft)' : 'white', color: filterCat === cat.key ? 'var(--orange)' : 'var(--dark)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {cat.label.charAt(0).toUpperCase() + cat.label.slice(1)}
            </button>
          ))}
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, background: 'white', borderRadius: 16 }}>
            <Package size={56} color="var(--gray-border)" style={{ marginBottom: 14 }} />
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No materials yet</div>
            <div style={{ color: 'var(--gray)', fontSize: 14, marginBottom: 20 }}>Add your first material to start receiving orders</div>
            <button onClick={openAdd} className="btn-primary"><Plus size={15} /> Add First Material</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
            {filtered.map(mat => (
              <div key={mat._id} style={{ background: 'white', borderRadius: 14, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
                <div className="owner-material-header" style={{ padding: '12px 16px', background: (mat.category === 'sand' ? '#FFFBEB' : mat.category === 'iron' ? '#F9FAFB' : 'var(--orange-soft)'), display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: 'white', border: '1px solid var(--gray-border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {mat.images?.[0] ? (
                      <img src={mat.images[0]} alt={mat.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Package size={20} color="var(--orange)" />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-main)' }}>{mat.name}</div>
                    {mat.brand && <div style={{ color: 'var(--gray)', fontSize: 12 }}>{mat.brand}</div>}
                  </div>
                  {mat.offer?.discount && <span className="badge badge-green">{mat.offer.discount}% OFF</span>}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 20, color: 'var(--orange)' }}>₹{mat.price.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--gray)' }}>/{mat.unit}</span></div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: mat.stock > 0 ? 'var(--green)' : 'var(--red)' }}>{mat.stock > 0 ? `${mat.stock} in stock` : 'Out of stock'}</span>
                  </div>
                  {mat.description && <p style={{ color: 'var(--gray)', fontSize: 12, lineHeight: 1.5, marginBottom: 10, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{mat.description}</p>}
                  {mat.variants?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                      {mat.variants.slice(0, 3).map((v, i) => <span key={i} className="tag" style={{ fontSize: 11 }}>{v.size}</span>)}
                      {mat.variants.length > 3 && <span className="tag" style={{ fontSize: 11 }}>+{mat.variants.length - 3} more</span>}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={() => toggleAvailability(mat)} title={mat.isAvailable !== false ? 'Hide from customers' : 'Publish for customers'} style={{ width: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1.5px solid var(--gray-border)', background: mat.isAvailable !== false ? '#ECFDF5' : 'var(--gray-light)', cursor: 'pointer', color: mat.isAvailable !== false ? 'var(--green)' : 'var(--gray)' }}>
                      {mat.isAvailable !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button onClick={() => openEdit(mat)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, border: '1.5px solid var(--gray-border)', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>
                      <Edit2 size={13} /> Edit
                    </button>
                    <button onClick={() => handleDelete(mat._id)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, border: '1.5px solid #FECACA', background: '#FEF2F2', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 32, width: '100%', maxWidth: 680, margin: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>{editId ? 'Edit Material' : 'Add New Material'}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'var(--gray-light)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="form-label">Category</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CATEGORIES.map(cat => (
                  <button key={cat} type="button" onClick={() => applyPreset(cat)}
                    style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${form.category === cat ? 'var(--orange)' : 'var(--gray-border)'}`, background: form.category === cat ? 'var(--orange-soft)' : 'white', color: form.category === cat ? 'var(--orange)' : 'var(--dark)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                    {cat.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Brand */}
            <div className="owner-material-pair" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Material Name *</label>
                <input className="form-input" placeholder={form.category === 'iron' ? 'e.g. Fe500D TMT Bar' : form.category === 'cement' ? 'e.g. UltraTech OPC 53' : 'Material name'} value={form.name} onChange={e => setField('name', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Brand / Company</label>
                <input className="form-input" placeholder={form.category === 'cement' ? 'e.g. UltraTech, Ambuja, ACC' : form.category === 'iron' ? 'e.g. TATA, JSW, Vizag' : 'Brand name'} value={form.brand} onChange={e => setField('brand', e.target.value)} />
              </div>
            </div>

            {/* Price, Unit, Stock */}
            <div className="owner-material-numbers" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Base Price (₹) *</label>
                <input className="form-input" type="number" placeholder="Price" value={form.price} onChange={e => setField('price', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Unit</label>
                <select className="form-input" value={form.unit} onChange={e => setField('unit', e.target.value)}>
                  {preset.units.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Stock Available *</label>
                <input className="form-input" type="number" placeholder="Quantity" value={form.stock} onChange={e => setField('stock', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Low Stock Alert</label>
                <input className="form-input" type="number" min="0" placeholder="5" value={form.lowStockThreshold} onChange={e => setField('lowStockThreshold', e.target.value)} />
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={2} placeholder="Describe this material..." value={form.description} onChange={e => setField('description', e.target.value)} style={{ resize: 'none' }} />
            </div>

            {/* Images */}
            <div className="form-group">
              <label className="form-label">Material Images</label>
              <div style={{ border: '1.5px dashed var(--gray-border)', borderRadius: 12, padding: 14, background: 'var(--gray-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: (form.images || []).length ? 12 : 0 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'white', border: '1.5px solid var(--gray-border)', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                    <Upload size={15} color="var(--orange)" />
                    Upload Images
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" multiple onChange={e => { addImages(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
                  </label>
                  <span style={{ color: 'var(--gray)', fontSize: 12 }}>Up to 8 images, max 2MB each</span>
                </div>
                {(form.images || []).length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(86px,1fr))', gap: 10 }}>
                    {form.images.map((image, index) => (
                      <div key={index} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 12, overflow: 'hidden', background: 'white', border: '1px solid var(--gray-border)' }}>
                        <img src={image} alt={`Material ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => removeImage(index)} style={{ position: 'absolute', top: 5, right: 5, width: 24, height: 24, borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #FECACA' }}>
                          <X size={13} />
                        </button>
                        {index === 0 && <span style={{ position: 'absolute', left: 5, bottom: 5, background: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: 7, padding: '2px 6px', fontSize: 10, fontWeight: 800 }}>Main</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Specifications */}
            {form.specifications?.length > 0 && (
              <div className="form-group">
                <label className="form-label">Specifications</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                  {form.specifications.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray)', minWidth: 60 }}>{s.key}:</span>
                      <input className="form-input" style={{ flex: 1 }} placeholder={s.key} value={s.value} onChange={e => updateSpec(i, 'value', e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="form-group">
              <label className="form-label">Tags (comma separated)</label>
              <input className="form-input" placeholder="e.g. bulk, quality, discount" value={form.tags} onChange={e => setField('tags', e.target.value)} />
            </div>

            {/* Offer */}
            <div style={{ background: '#ECFDF5', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--green)' }}>🏷️ Offer / Discount (Optional)</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label" style={{ fontSize: 12 }}>Discount %</label>
                  <input className="form-input" type="number" placeholder="e.g. 10" value={form.offer.discount} onChange={e => setField('offer', { ...form.offer, discount: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="form-label" style={{ fontSize: 12 }}>Valid Till</label>
                  <input className="form-input" type="date" value={form.offer.validTill} onChange={e => setField('offer', { ...form.offer, validTill: e.target.value })} />
                </div>
              </div>
            </div>

            {/* Variants */}
            <div style={{ border: '1.5px solid var(--gray-border)', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
              <button type="button" onClick={() => setShowVariants(!showVariants)}
                style={{ width: '100%', padding: '12px 16px', background: 'var(--gray-light)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 600, fontSize: 14 }}>
                <span>Variants / Sizes ({form.variants.length})</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {preset.variants?.length > 0 && (
                    <span onClick={(e) => { e.stopPropagation(); loadPresetVariants(); }}
                      style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 600 }}>Load {form.category} presets</span>
                  )}
                  {showVariants ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </button>
              {showVariants && (
                <div style={{ padding: 16 }}>
                  {form.variants.map((v, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input className="form-input" placeholder="Size / Name" value={v.size} onChange={e => updateVariant(i, 'size', e.target.value)} style={{ fontSize: 13 }} />
                      <input className="form-input" type="number" placeholder="Price ₹" value={v.price} onChange={e => updateVariant(i, 'price', e.target.value)} style={{ fontSize: 13 }} />
                      <input className="form-input" type="number" placeholder="Stock" value={v.stock} onChange={e => updateVariant(i, 'stock', e.target.value)} style={{ fontSize: 13 }} />
                      <input className="form-input" placeholder="SKU" value={v.sku} onChange={e => updateVariant(i, 'sku', e.target.value)} style={{ fontSize: 13 }} />
                      <button onClick={() => removeVariant(i)} style={{ background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '8px', cursor: 'pointer', color: 'var(--red)', display: 'flex' }}><X size={14} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={addVariant} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--orange)', background: 'none', border: '1.5px dashed var(--orange)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginTop: 4 }}>
                    <Plus size={14} /> Add Variant
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowForm(false)} className="btn-outline" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button onClick={handleSave} className="btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                <Save size={15} /> {saving ? 'Saving...' : (editId ? 'Update Material' : 'Add Material')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
